import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { theme } from '../../styles/theme';
import TableFilters from './TableFilters';

const AuditScheduleTable = ({ refreshTrigger }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('scheduled_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    status: '',
    auditorOrg: '',
    scheduledDate: '',
    scheduledDateEnd: '',
    dueDate: '',
    dueDateEnd: '',
    search: ''
  });

  useEffect(() => {
    fetchSchedules();
  }, [sortField, sortDirection, filters, refreshTrigger]);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      console.log('Fetching schedules with filters:', filters);

      let query = supabase
        .from('audit_schedules_with_details')
        .select('*')
        .order(sortField, { ascending: sortDirection === 'asc' });

      if (schedules.length > 5) {
        if (filters.status) {
          query = query.eq('schedule_status', filters.status);
        }

        if (filters.auditorOrg) {
          query = query.eq('auditor_org', filters.auditorOrg);
        }

        if (filters.scheduledDate) {
          query = query.gte('scheduled_date', filters.scheduledDate);
        }

        if (filters.scheduledDateEnd) {
          query = query.lte('scheduled_date', filters.scheduledDateEnd);
        }

        if (filters.dueDate) {
          query = query.gte('due_date', filters.dueDate);
        }

        if (filters.dueDateEnd) {
          query = query.lte('due_date', filters.dueDateEnd);
        }

        if (filters.search) {
          query = query.or(
            `item.ilike.%${filters.search}%,` +
            `vendor_name.ilike.%${filters.search}%,` +
            `station_iata_code.ilike.%${filters.search}%,` +
            `location.ilike.%${filters.search}%,` +
            `lead_auditor_email.ilike.%${filters.search}%,` +
            `lead_auditor_email_external.ilike.%${filters.search}%`
          );
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      setSchedules(data || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setError('Failed to load audit schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const colors = {
      'Not Scheduled': 'gray',
      'Scheduled': 'blue',
      'Deferred': 'yellow',
      'Confirmed': 'green',
      'Overdue': 'red',
      'Performed': 'indigo',
      'Closed': 'purple',
      'Cancelled': 'red'
    };
    return colors[status] || 'gray';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (!schedules.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        No audit schedules found
      </div>
    );
  }

  return (
    <div>
      {schedules.length > 5 && (
        <TableFilters filters={filters} onFilterChange={setFilters} />
      )}
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('item')}
              >
                Item ID {sortField === 'item' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('scheduled_date')}
              >
                Scheduled Date {sortField === 'scheduled_date' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('auditor_org')}
              >
                Auditor Org {sortField === 'auditor_org' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Purpose
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('station_iata_code')}
              >
                Station/Location {sortField === 'station_iata_code' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Vendor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lead Auditor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {schedules.map((schedule) => (
              <tr key={schedule.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {schedule.item || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(schedule.scheduled_date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {schedule.auditor_org}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {schedule.purpose}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="font-medium">{schedule.station_iata_code || '-'}</div>
                  {schedule.location && (
                    <div className="text-gray-500 text-xs mt-1">{schedule.location}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {schedule.vendor_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`
                    px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                    bg-${getStatusColor(schedule.schedule_status)}-100
                    text-${getStatusColor(schedule.schedule_status)}-800
                  `}>
                    {schedule.schedule_status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {schedule.lead_auditor_email || schedule.lead_auditor_email_external || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {schedule.duration} days
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditScheduleTable;
